import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getElections } from '../services/api';
import './Pages.css';

const ElectionsPage = () => {
  const [elections, setElections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchElections = async () => {
      try {
        setLoading(true);
        const response = await getElections();
        setElections(response.data.elections || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching elections:', err);
        setError('Failed to load elections. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchElections();
  }, []);

  const getElectionTypeLabel = (type) => {
    switch (type) {
      case 1: return 'Single Choice';
      case 2: return 'Multi Choice';
      case 3: return 'Ranked Choice';
      default: return 'Unknown';
    }
  };

  const getElectionStatus = (election) => {
    const now = new Date();
    
    if (election.end_time && new Date(election.end_time) < now) {
      return { status: 'Ended', className: 'status-ended' };
    }
    
    if (election.start_time && new Date(election.start_time) > now) {
      return { status: 'Upcoming', className: 'status-upcoming' };
    }
    
    return { status: 'Active', className: 'status-active' };
  };

  return (
    <div className="elections-page">
      <div className="page-header">
        <h1>Elections</h1>
        <Link to="/elections/create" className="btn btn-primary">Create Election</Link>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading elections...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="btn btn-outline" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      ) : elections.length === 0 ? (
        <div className="empty-state">
          <h3>No elections found</h3>
          <p>Create your first election to get started</p>
          <Link to="/elections/create" className="btn btn-primary">Create Election</Link>
        </div>
      ) : (
        <div className="elections-grid">
          {elections.map((election) => {
            const { status, className } = getElectionStatus(election);
            
            return (
              <div key={election.id} className="election-card">
                <div className="election-card-header">
                  <h3 className="election-title">{election.title}</h3>
                  <span className={`election-status ${className}`}>{status}</span>
                </div>
                
                <div className="election-details">
                  <p className="election-description">{election.description || 'No description provided.'}</p>
                  
                  <div className="election-meta">
                    <div className="meta-item">
                      <span className="meta-label">Type:</span>
                      <span className="meta-value">{getElectionTypeLabel(election.election_type)}</span>
                    </div>
                    
                    <div className="meta-item">
                      <span className="meta-label">Candidates:</span>
                      <span className="meta-value">{election.max_candidates}</span>
                    </div>
                    
                    <div className="meta-item">
                      <span className="meta-label">Votes:</span>
                      <span className="meta-value">{election.ballotCount || 0}</span>
                    </div>
                  </div>
                </div>
                
                <div className="election-actions">
                  <Link to={`/elections/${election.id}`} className="btn btn-outline">View Details</Link>
                  
                  {status === 'Active' && (
                    <Link to={`/elections/${election.id}/vote`} className="btn btn-primary">Vote Now</Link>
                  )}
                  
                  {status === 'Ended' && (
                    <Link to={`/elections/${election.id}/results`} className="btn btn-secondary">View Results</Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ElectionsPage;